import { Button, Card, CardContent, CardHeader, CardTitle } from '@1trading/react';
import { ChartSpline, Pause, Play, Square } from 'lucide-react';
import { StatusChip } from '@/components/ui/status-chip';
import type { MarketMakerState, MarketStatus } from '@/services/api/market-maker';
import { makerStatusVariant, PURPLE_CHIP_CLASS } from './status';

interface ActionRowProps {
  label: string;
  onAction: (state: MarketMakerState) => void;
}

// Fixed-width label (~44 px) keeps the Maker/Takers button columns aligned.
// No confirmation dialogs — every control fires immediately (non-prod tooling).
const ActionRow = ({ label, onAction }: ActionRowProps) => (
  <div className="flex items-center gap-1">
    <span className="w-11 shrink-0 text-xs text-muted-foreground">{label}</span>
    <Button className="gap-1.5 text-emerald-500" onClick={() => onAction('start')} size="sm" type="button" variant="ghost">
      <Play className="size-3.5" />
      Start
    </Button>
    <Button className="gap-1.5 text-amber-500" onClick={() => onAction('pause')} size="sm" type="button" variant="ghost">
      <Pause className="size-3.5" />
      Pause
    </Button>
    <Button className="gap-1.5 text-red-500" onClick={() => onAction('stop')} size="sm" type="button" variant="ghost">
      <Square className="size-3.5" />
      Stop
    </Button>
  </div>
);
ActionRow.displayName = 'ActionRow';

interface MarketCardProps {
  market: MarketStatus;
  onMakerAction: (state: MarketMakerState) => void;
  onTakerAction: (state: MarketMakerState) => void;
}

export const MarketCard = ({ market, onMakerAction, onTakerAction }: MarketCardProps) => (
  <Card className="flex h-full flex-col">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-base font-semibold">{market.market}</CardTitle>
      <StatusChip className="px-2 py-0.5 text-xs" variant={makerStatusVariant(market.makerStatus)}>
        {market.makerStatus}
      </StatusChip>
    </CardHeader>
    <CardContent className="flex flex-1 flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Mid Price:</span>
        {/* Rendered verbatim — the raw decimal string, no formatting */}
        <span className="font-medium tabular-nums">{market.lastMidPrice ?? 'N/A'}</span>
      </div>
      {market.takerStatuses.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm text-muted-foreground">Takers:</span>
          {market.takerStatuses.map((taker) => (
            // accountId is never displayed (legacy: key only); tooltip is a flagged deviation
            <StatusChip className="px-1.5 py-0 text-xs" key={taker.accountId} variant={makerStatusVariant(taker.status)}>
              <span title={taker.accountId}>{taker.status}</span>
            </StatusChip>
          ))}
        </div>
      ) : null}
      {market.hasSimulation ? (
        <div>
          <StatusChip className={`px-2 py-0.5 text-xs ${PURPLE_CHIP_CLASS}`} icon={ChartSpline}>
            Simulation Active
          </StatusChip>
        </div>
      ) : null}
      <div className="mt-auto flex flex-col gap-1 pt-1">
        <ActionRow label="Maker" onAction={onMakerAction} />
        {market.takerStatuses.length > 0 ? <ActionRow label="Takers" onAction={onTakerAction} /> : null}
      </div>
    </CardContent>
  </Card>
);
MarketCard.displayName = 'MarketCard';
